import { useState } from 'react';

import { QuizContentData } from '../../../types/contentTypes';
import { textToParagraphs } from '../../../utils/functions';
import IconButton from '../../atoms/button/IconButton';
import OptionListItem from '../../atoms/option-list-item/OptionListItem';
import DiscussionNudge from '../../molecules/nudge/DiscussionNudge';
import AnswerStatus from '../AnswerStatus';

import './QuizContent.scss';

interface QuizContentProps {
  content: QuizContentData;
  onComplete: (() => void) | undefined;
  isPreview?: boolean;
}

export default function QuizContent({ content: quiz, onComplete, isPreview = false }: QuizContentProps) {
  const [markedAnswerIndex, setMarkedAnswerIndex] = useState(-1);
  const [confirmedAnswerIndex, setConfirmedAnswerIndex] = useState(-1);
  const correctAnswerIndex = quiz.answerAlternatives.findIndex(alternative => alternative.correct);

  const hasAnsweredCorrectly = confirmedAnswerIndex !== -1 && confirmedAnswerIndex === correctAnswerIndex;
  const hasConfirmedAnswer = confirmedAnswerIndex !== -1;
  const hasMarkedAnswerButNotConfirmed = markedAnswerIndex !== -1 && confirmedAnswerIndex === -1;

  // Methods
  async function markAnswer(answerIndex: number) {
    if (answerIndex === markedAnswerIndex) return;
    setMarkedAnswerIndex(answerIndex);
    setConfirmedAnswerIndex(-1);
  }

  async function confirmAnswer() {
    setConfirmedAnswerIndex(markedAnswerIndex);
    if (markedAnswerIndex === correctAnswerIndex) {
      if (onComplete) onComplete();
    }
  }

  const OptionsList = quiz.answerAlternatives?.map((alternative, index) => {
    return (
      <OptionListItem
        key={alternative.key}
        text={alternative.answer}
        onClick={!hasAnsweredCorrectly ? () => markAnswer(index) : undefined}
        checked={markedAnswerIndex === index}
        disabled={hasAnsweredCorrectly}
      />
    );
  });

  return (
    <section className="quiz-content">
      <h2 className="strong-heading mb-8">{quiz.headline}</h2>
      <div className="mb-12">{textToParagraphs(quiz.description)}</div>
      <p className="mb-6">{quiz.question}</p>
      <ul className="quiz-list mb-5">{OptionsList}</ul>

      {!isPreview ? (
        <div className="status-container">
          {hasConfirmedAnswer && (
            <>
              <AnswerStatus
                className="mb-auto"
                isCorrect={hasAnsweredCorrectly}
                titleText={hasAnsweredCorrectly ? 'Correct!' : 'Incorrect!'}
              >
                <p>{quiz.answerAlternatives[confirmedAnswerIndex].feedback}</p>
              </AnswerStatus>
              <DiscussionNudge />
            </>
          )}

          {hasMarkedAnswerButNotConfirmed && !isPreview && (
            <IconButton
              className="block mx-auto mt-5 confirm-button"
              icon="check-circle"
              label="Confirm answer"
              onClick={confirmAnswer}
            />
          )}
        </div>
      ) : (
        <p className="info-caption">The quiz is read-only since this is a preview</p>
      )}
    </section>
  );
}
